import {
    type ComponentType,
    useRef,
    type FC
} from "react";
import {
    ScrollView,
    View
} from "react-native";
import type ICarouselIndicatorsProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    CarouselKeyboardEventType,
    CarouselPartContentProps
} from "../../type";
import {
    renderAccessibleText
} from "../../../../helpers/accessibility";
import {
    getControlMeasures
} from "../../../../helpers/theme/control";
import {
    formatNumber
} from "../../../../helpers/locale";
import {
    withCarouselContext
} from "../../context";
import {
    getCarouselKeyStep
} from "../../util";
import CarouselThumbnail from "../../internal/thumbnail";
import CarouselDot from "../../internal/dot";
import Skeleton from "../../../skeleton";
import Text from "../../../text";

const CarouselIndicatorsContent: FC<CarouselPartContentProps<ICarouselIndicatorsProps>> = ({
    renderThumbnail: renderThumbnailProp,
    customTheme: customThemeProp,
    "aria-label": ariaLabel,
    type: typeProp,
    context,
    style
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const itemRefs = useRef<Array<View | null>>([]);

    const type = typeProp ?? context.indicator;

    const renderThumbnail = renderThumbnailProp ?? context.renderThumbnail;

    const pageCount = context.metrics.pageCount;

    const currentSize = getControlMeasures({
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes
    });

    const {
        thumbs: thumbsDynamicStyle,
        badge: badgeDynamicStyle
    } = useStyles({
        shapeConfigs,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    });

    const formatCount = (value: number) => formatNumber({
        locale: activeLocale,
        value
    });

    const onKeyDown = (event: CarouselKeyboardEventType) => {
        if(event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        const step = getCarouselKeyStep({
            orientation: context.orientation,
            direction: context.direction,
            key: event.key
        });

        let target = context.activeIndex + step;

        if(event.key === "Home") {
            target = 0;
        } else if(event.key === "End") {
            target = pageCount - 1;
        } else if(!step) {
            return;
        }

        target = Math.max(0, Math.min(target, pageCount - 1));

        event.preventDefault();

        event.stopPropagation?.();

        context.goTo(target);

        itemRefs.current[target]?.focus();
    };

    const getPageLabel = (index: number) => localize("go-to-slide-x-of-y", [
        formatCount(index + 1),
        formatCount(pageCount)
    ]);

    const renderCounter = () => {
        const start = context.visibleRange.start + 1;

        const end = Math.min(context.visibleRange.end + 1, context.count);

        const isRange = end > start;

        const current = isRange ? `${formatCount(start)}–${formatCount(end)}` : formatCount(start);

        return renderAccessibleText({
            label: isRange ? localize("x-to-y-of-z", [
                formatCount(start),
                formatCount(end),
                formatCount(context.count)
            ]) : localize("x-of-y", [
                current,
                formatCount(context.count)
            ]),
            children: <>
                <Text
                    variant={currentSize.labelVariant}
                    customTheme={customTheme}
                    color="high"
                >
                    {current}
                </Text>
                {` / ${formatCount(context.count)}`}
            </>,
            variant: currentSize.labelVariant,
            style: [
                style,
                stylesheet.counter
            ],
            customTheme,
            color: "mid"
        });
    };

    const renderBadge = () => {
        return <View
            style={[
                style,
                stylesheet.badge,
                badgeDynamicStyle
            ]}
            aria-hidden={true}
        >
            <Text
                style={stylesheet.counter}
                customTheme={customTheme}
                variant="labelSmallSize"
                color="contrastHigh"
            >
                {localize("x-of-y", [
                    formatCount(context.activeIndex + 1),
                    formatCount(context.count)
                ])}
            </Text>
        </View>;
    };

    const renderDots = () => {
        return <View
            style={[
                style,
                context.orientation === "vertical" ? stylesheet.dotsColumn : stylesheet.dotsRow
            ]}
            aria-label={ariaLabel ?? localize("select-slide")}
            role="tablist"
        >
            {
                Array.from({
                    length: pageCount
                }, (_, index) => <CarouselDot
                    isSelected={index === context.activeIndex}
                    ref={(node) => {
                        itemRefs.current[index] = node;
                    }}
                    onPress={() => context.goTo(index)}
                    orientation={context.orientation}
                    label={getPageLabel(index)}
                    customTheme={customTheme}
                    onKeyDown={onKeyDown}
                    key={index}
                />)
            }
        </View>;
    };

    const renderThumbnails = () => {
        return <ScrollView
            aria-label={ariaLabel ?? localize("select-slide")}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={[
                stylesheet.thumbs,
                thumbsDynamicStyle
            ]}
            horizontal={true}
            role="tablist"
            style={style}
        >
            {
                Array.from({
                    length: pageCount
                }, (_, index) => <CarouselThumbnail
                    isSelected={index === context.activeIndex}
                    ref={(node) => {
                        itemRefs.current[index] = node;
                    }}
                    onPress={() => context.goTo(index)}
                    label={getPageLabel(index)}
                    customTheme={customTheme}
                    onKeyDown={onKeyDown}
                    key={index}
                >
                    {renderThumbnail?.(index)}
                </CarouselThumbnail>)
            }
        </ScrollView>;
    };

    if(context.isLoading) {
        return <Skeleton
            width={controlHeights.small * 2}
            height={borders.indicator * 2}
            customTheme={customTheme}
            radius="full"
        />;
    }

    if(type === "none" || pageCount <= 1) {
        return null;
    }

    if(type === "counter") {
        return renderCounter();
    }

    if(type === "badge") {
        return renderBadge();
    }

    if(type === "thumbnails") {
        return renderThumbnails();
    }

    return renderDots();
};

const CarouselIndicators: ComponentType<ICarouselIndicatorsProps> = withCarouselContext("indicators", CarouselIndicatorsContent);
export default CarouselIndicators;
